
import cloneDeep from "lodash-es/cloneDeep";
import get from "lodash-es/get";
import find from "lodash-es/find";
import map from "lodash-es/map";
import every from "lodash-es/every";
import sortBy from "lodash-es/sortBy";
import last from "lodash-es/last";
import { Memoize } from "lodash-decorators";

import "es6-promise";

import axios, { AxiosResponse } from "axios";
import Vue, { ComponentOptions } from "vue";
import Component from "vue-class-component";
import {
  Emit,
  Inject,
  Model,
  Prop,
  Provide,
  Watch
} from "vue-property-decorator";

import { Get, Sync, Call } from "vuex-pathify";

import { SET_SAMPLES, CLEAR_SAMPLE_CART } from "../../../../store";

import { Sample, SampleCartItems } from "../../../../model";
import { WebAPI } from "../../../../web-api";

//import { DummySampleList as _dummySampleList } from "../../../../test-data";
//import { DummyPipelineConfig as _dummyPipelineConfig } from "../../../../test-data";
import { Snackbar } from "../../../../snackbar";
import BannerNotice from "../../../BannerNotice.vue";
//import RemoteFilesSelect from "../../../RemoteSelect/RemoteFilesSelect.vue";
//import { FileListItem } from "../../../../file-tree-util";
//import { filenameFromUrl } from "../../../../util";
import { ReferenceGenome, ILaxyFile, PairedEndFiles } from "../../../../types";

@Component({
  components: {
    BannerNotice
    //RemoteFilesSelect
  },
  props: {},
  filters: {},
  beforeRouteLeave(to: any, from: any, next: any) {
    (this as any).beforeRouteLeave(to, from, next);
  }
})
export default class PipelineParams extends Vue {
  public pipeline_name: string = "rnasik";

  @Prop({ default: true, type: Boolean })
  public showButtons: boolean;
  public show_advanced = false;

  public submitting: boolean = false;
  public error_alert_message: string = "Everything is fine. 🏩";
  public snackbar_message: string = "Everything is fine. ௐ";
  public snackbar_duration: number = 2000;

  public pipelinerun_uuid: string | null = null;
  public selectedSamples: Array<Sample> = [];

  public reference_genome_valid: boolean = true;
  public available_genomes: Array<ReferenceGenome> | null = null;

  get pipeline_versions() {
    // const versions = = ["1.5.3", "1.5.2", "1.5.3-laxydev", "1.5.4"];
    return get(
      this.$store.state.availablePipelines[this.pipeline_name],
      "metadata.versions",
      []
    );
  }

  get default_pipeline_version() {
    return get(
      this.$store.state.availablePipelines[this.pipeline_name],
      "metadata.default_version",
      last(
        sortBy(this.pipeline_versions, v => {
          return v;
        })
      )
    );
  }

  public pipeline_aligners = [
    { text: "STAR", value: "star" },
    { text: "BWA-MEM", value: "bwa" }
  ];

  public _samples: SampleCartItems;
  get samples(): SampleCartItems {
    this._samples = cloneDeep(this.$store.state.samples);
    // We trigger the computed property so that if samples change validation runs
    const _ = this.isValid_params;
    return this._samples;
  }

  @Sync("pipelineParams@description")
  public description: string;

  @Sync("pipelineParams@pipeline_version")
  public pipeline_version: string;

  get pipeline_aligner() {
    return (
      this.$store.getters.pipelineParams.pipeline_aligner ||
      this.pipeline_aligners[0].value
    );
  }

  set pipeline_aligner(aligner: string) {
    let state = Object.assign({}, this.$store.state.pipelineParams);
    state.pipeline_aligner = aligner;
    this.$store.set("pipelineParams", state);
  }

  created() {
    this._samples = cloneDeep(this.$store.state.samples);
  }

  mounted() {
    this.pipeline_version = this.default_pipeline_version;
  }

  /*
   *  Populates the pipelineParams.fetch_files list with files
   *  that should be retrieved by the backend as initial input files.
   *  For RNAsik runs, this is the reference genome files and input
   *  FASTQ files.
   *
   *  TODO: Deprecate sample_cart on the backend and just use this.
   *        Keep using the state.samples data structure here on the
   *        frontend for convenience, but convert it's content into
   *        fetch_files for use by pipeline_config.json / laxydl.
   */
  updateFetchFiles() {
    if (!this.$store.state.use_custom_genome) {
      this.$store.set("pipelineParams@user_genome.fasta_url", "");
      this.$store.set("pipelineParams@user_genome.annotation_url", "");
    }

    const fetch_files = this.$store.get(
      "pipelineParams/generateFetchFilesList"
    );
    let params = this.$store.copy("pipelineParams");
    params.fetch_files = fetch_files;
    this.$store.set("pipelineParams", params);
  }

  prepareData() {
    this.updateFetchFiles();

    let data = {
      sample_cart: this.$store.state.samples.id,
      params: this.$store.getters.pipelineParams,
      pipeline: this.pipeline_name,
      description: this.description
    };

    data = cloneDeep(data);

    if (this.$store.state.use_custom_genome) {
      data.params.genome = null;
    }
    return data;
  }

  get isValid_reference_genome() {
    return this.$store.get("pipelineParams/isValidReferenceGenome");
  }

  get isValid_samples_added() {
    // return (this.samples.items.length != 0);
    return this.$store.getters.sample_cart_count > 0;
  }

  get isValid_duplicate_samples() {
    const samples = this.$store.state.samples;
    const seen: string[] = [];
    for (let i of samples.items) {
      for (let f of i.files) {
        for (let pair of ["R1", "R2"]) {
          if (f[pair] == null) continue;
          if (f[pair].location == null) continue;
          if (seen.includes(f[pair].location)) return false;
          seen.push(f[pair].location);
        }
      }
    }

    return true;
  }

  get isValid_mixed_single_paired_check() {
    function isPairedEnd(f: PairedEndFiles) {
      return f.R1 != null && f.R2 != null;
    }
    const samples = this.$store.state.samples;
    let is_paired: boolean[] = [];
    for (let i of samples.items) {
      for (let f of i.files) {
        is_paired.push(isPairedEnd(f));
      }
    }

    // true for all paired or all single, not a mixture
    return every(is_paired) || every(map(is_paired, b => !b));
  }

  get isValid_params() {
    let is_valid = false;
    if (
      this.isValid_reference_genome &&
      this.isValid_samples_added &&
      this.isValid_duplicate_samples &&
      this.isValid_mixed_single_paired_check
    ) {
      is_valid = true;
    }
    this.$store.set("pipelineParams_valid", is_valid);

    return is_valid;
  }

  async save() {
    try {
      this.submitting = true;
      await this.$store.dispatch(SET_SAMPLES, this._samples);

      const data = this.prepareData();
      // console.log(data);

      if (this.pipelinerun_uuid == null) {
        const response = (await WebAPI.fetcher.post(
          "/api/v1/pipelinerun/",
          data
        )) as AxiosResponse;
        this.pipelinerun_uuid = response.data.id;
      } else {
        (await WebAPI.fetcher.put(
          `/api/v1/pipelinerun/${this.pipelinerun_uuid}/`,
          data
        )) as AxiosResponse;
      }
      this.submitting = false;
      Snackbar.flashMessage("Saved !");
    } catch (error) {
      console.log(error);
      this.submitting = false;
      this.error_alert_message = error.toString();
      this.openDialog("error_dialog");
    }
  }

  async run() {
    try {
      this.submitting = true;
      await this.save();
      this.submitting = true;

      if (!this.isValid_params) {
        Snackbar.flashMessage("Please correct errors before submitting.");
        this.submitting = false;
        return null;
      }

      // if (this.samples.items.length == 0) {
      //     console.log("NO samples !!");
      //     // Snackbar.flashMessage("Please add some samples to the sample cart first !");
      //     this.error_alert_message = "Please add some samples to the sample cart first !";
      //     this.openDialog("error_dialog");
      //     // throw Error( "Please add some samples to the sample cart first !");
      //     return null;
      // }

      try {
        let response = null;
        response = (await WebAPI.fetcher.post(
          `/api/v1/job/?pipeline_run_id=${this.pipelinerun_uuid}`,
          {}
        )) as AxiosResponse;
        this.submitting = false;
        Snackbar.flashMessage("Saved !");
        await this.clearCart();
        return response;
      } catch (error) {
        console.log(error);
        this.submitting = false;
        this.error_alert_message = error.toString();
        this.openDialog("error_dialog");
      }
    } catch (error) {
      console.error(error);
    }

    this.submitting = false;
    return null;
  }

  async clearCart() {
    await this.$store.dispatch(CLEAR_SAMPLE_CART);
  }

  openDialog(ref: string) {
    (this.$refs[ref] as MdDialog).open();
  }

  closeDialog(ref: string) {
    (this.$refs[ref] as MdDialog).close();
  }

  onSelect(rows: any) {
    this.selectedSamples = rows as Array<Sample>;
  }

  beforeRouteLeave(to: any, from: any, next: any) {
    // console.log([to, from, next]);
    this.$store.set("samples", this._samples);
    next();
  }
}
